import { Button, CSSObject, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Slide, Theme, styled } from "@mui/material";
import MuiDrawer from '@mui/material/Drawer';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
// import RuleIcon from '@mui/icons-material/Rule';
import { TbTargetArrow } from 'react-icons/tb';
import { BiBook } from 'react-icons/bi'
import { useLocation, useNavigate } from "react-router-dom";
import { ReactElement, useEffect, useState, useRef } from "react";
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import qualityCheckApi from "store/apis/qualityCheckApi";
import ConstructionIcon from '@mui/icons-material/Construction';
import ClearIcon from '@mui/icons-material/Clear';
import { qualityCheckMyGlossary } from "types/models/QualityCheckMyGlossary";
import { FiUpload } from 'react-icons/fi'
import { GlossaryItem, loadPairList } from "shared/glossaryutils";
import GlossaryPanel from "./GlossaryPanel";
import BuildGlossaryPanel from "../containers/BuildGlossaryPanel";
import { TransitionGroup } from 'react-transition-group';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { RootState } from "store/store";
import { useSelector } from "react-redux"
import FunctionsIcon from '@mui/icons-material/Functions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
// import VirtualizedEditGlossaryPanel from "./VirtualizedEditGlossaryPanel";
import { useTranslation } from 'react-i18next';

type SideMenuProps = {
    isOpen: boolean;
    toggleSideBar: () => void;
}

const paymentPageSetting: Record<string, { icon: ReactElement; link: string }> = {
    "Addcuracy Plus+": {
        icon: <DiamondOutlinedIcon style={{ height: "24px" }} />,
        link: "/subscription",
    },
    "Top Up": {
        icon: <CreditCardIcon style={{ height: "24px", width: "24px" }} />,
        link: "/top-up",
    }
}

export type MainTool = 'CALL_FIGURES' | 'GLOSSARY_CHECK' | 'TABLE_SUMS_CHECK';

type MainToolConfig = {
    icon: ReactElement;
    mode: string;
    link: string;
    labelKey: string;
    descriptionKey: string;
}

export const mainToolConfig: Record<MainTool, MainToolConfig> = {
    CALL_FIGURES: {
        icon: <CalculateOutlinedIcon />,
        mode: 'CALLFIGURE',
        link: '/',
        labelKey: 'sideMenu.callFigures',
        descriptionKey: 'sideMenu.description.callFigures',
    },
    GLOSSARY_CHECK: {
        icon: <BiBook style={{ height: "24px", width: "24px" }} />,
        mode: 'GLOSSARY_CHECKING',
        link: '/glossary_check',
        labelKey: 'sideMenu.glossaryCheck',
        descriptionKey: 'sideMenu.description.glossaryCheck',
    },
    // ACCURACY_CHECK: {
    //     icon: <TbTargetArrow style={{ height: "24px", width: "24px" }} />,
    //     mode: 'ACCURACY_CHECKING',
    //     link: '/accuracy_check',
    //     labelKey: 'sideMenu.accuracyCheck',
    //     descriptionKey: 'sideMenu.description.accuracyCheck',
    // },
    TABLE_SUMS_CHECK: {
        icon: <FunctionsIcon />,
        mode: 'CHECK_SUMS',
        link: '/check_sums',
        labelKey: 'sideMenu.tableSumsCheck',
        descriptionKey: 'sideMenu.description.tableSumsCheck',
    },
    // SIGN_OFF_CHECK: {
    //     icon: <AttachMoneyIcon />,
    //     mode: 'SIGN_OFF_CHECK',
    //     link: '/sign-off_check',
    //     labelKey: 'sideMenu.signOffCheck',
    //     descriptionKey: 'sideMenu.description.signOffCheck',
    // },
}

const openedMixin = (theme: Theme): CSSObject => ({
    width: 265,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: 265,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const SideMenu = (props: SideMenuProps) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const location = useLocation();

    const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);

    const [isExpand, setIsExpand] = useState(false);
    const [isOpenDeleteConfirmDialog, setIsOpenDeleteConfirmDialog] = useState(false);
    const [isOpenEditGlossary, setIsOpenEditGlossary] = useState(false);
    const [isOpenCreateGlossary, setIsOpenCreateGlossary] = useState(false);
    const [isOpenBuildGlossaryPanel, setIsOpenBuildGlossaryPanel] = useState(false);
    const [selectedGlossaryId, setSelectedGlossaryId] = useState(-1);
    const [glossaryList, setGlossaryList] = useState<GlossaryItem[]>([]);
    const [glossaryName, setGlossaryName] = useState<string>("");

    const fileUploadFieldRef = useRef<HTMLInputElement>(null);

    const { data } = qualityCheckApi.useQualityCheckMyGlossaryListQuery();
    
    // Derive selectedGlossaryItem from RTK Query data - auto-updates when list refetches
    const selectedGlossaryItem = data?.find(g => g.myGlossaryId === selectedGlossaryId);
    const [deleteMyGlossary] = qualityCheckApi.useQualityCheckMyGlossaryDeleteMutation();

    useEffect(() => {
        if (!isLoggedIn || !props.isOpen) {
            setIsExpand(false);
        }
    }, [isLoggedIn, props.isOpen])

    const handleExpandMyGlossary = () => {
        if (!props.isOpen) {
            props.toggleSideBar();
        }
        setIsExpand(!isExpand);
    }

    const importMyGlossaryOnClick = () => {
        if (!props.isOpen) {
            props.toggleSideBar();
        }
        fileUploadFieldRef.current && fileUploadFieldRef.current.click()
    }

    const handleMyglossaryOnClick = (glossary: qualityCheckMyGlossary) => {
        setSelectedGlossaryId(glossary.myGlossaryId);
        setIsOpenEditGlossary(true);
    }

    const buildGlossaryOnClick = () => {
        if (!props.isOpen) {
            props.toggleSideBar();
        }
        setIsOpenBuildGlossaryPanel(true);
    }


    const handleMyGlossaryFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files;
        if (file) {
            const glossaryItems = await loadPairList(file[0]);
            setGlossaryList(glossaryItems)
            setGlossaryName(file[0].name)
            setIsOpenCreateGlossary(true)
            // const glossaryName = file[0].name;
            // await importMyGlossary({ glossaryName: glossaryName, glossaryItems: glossaryItems }).unwrap().then(() => {
            //     refetch();
            //     toast.success("My glossary import is successed");
            // }).catch((error) => {
            //     toast.error(error.data.message);
            // });
        }
    }

    const deleteConfirmDialog = () => {
        return (
            <Dialog
                open={isOpenDeleteConfirmDialog}
            >
                <DialogTitle>
                    {t('sideMenu.deleteGlossary')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('sideMenu.confirmDelete', { name: data?.filter((glossary) => glossary.myGlossaryId === selectedGlossaryId).map((glossary) => glossary.glossaryName) })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setIsOpenDeleteConfirmDialog(false); setSelectedGlossaryId(-1); }}>
                        {t('sideMenu.cancel')}
                    </Button>
                    <Button onClick={() => { 
                        deleteMyGlossary({ myGlossaryId: selectedGlossaryId }); 
                        setIsOpenDeleteConfirmDialog(false);
                        setSelectedGlossaryId(-1);
                    }}>
                        {t('sideMenu.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const renderListItem = (entry: [MainTool, MainToolConfig], index: number, action: (path: string) => void) => {
        const setting = entry[1];

        return (
            <ListItem disablePadding key={index}>
                <ListItemButton
                    onClick={() => action(setting.link)}
                    sx={{
                        minHeight: 48,
                        justifyContent: props.isOpen ? 'initial' : 'center',
                        px: 2.5,
                        backgroundColor: location.pathname === setting.link ? "black" : "white",
                        ":hover": {
                            backgroundColor: "black",
                            "& .MuiListItemText-root": {
                                color: "white"
                            },
                            "& .MuiListItemIcon-root": {
                                color: "white"
                            }
                        }
                    }}
                    disabled={false}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: props.isOpen ? 3 : 'auto',
                            justifyContent: 'center',
                            color: location.pathname === setting.link ? "white" : "black"
                        }}
                    >
                        {setting.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={t(setting.labelKey)}
                        sx={{ opacity: props.isOpen ? 1 : 0, color: location.pathname === setting.link ? 'white' : 'black' }}
                    />
                </ListItemButton>
            </ListItem>
        )
    }

    const renderMainFunction = () => {

        const handleMainFunctionOnClick = (path: string) => {
            if (!props.isOpen) {
                props.toggleSideBar();
            }
            navigate(path);
        }

        return (
            <List>
                <TransitionGroup>
                    <Collapse>
                        {props.isOpen &&
                            <Slide direction={"right"} in={props.isOpen} mountOnEnter unmountOnExit >
                                <ListSubheader sx={{ fontWeight: "1000", color: "black", lineHeight: "25px" }}>
                                    {t('sideMenu.toolsHeader')}
                                </ListSubheader>
                            </Slide>}
                    </Collapse>
                    {(Object.entries(mainToolConfig) as [MainTool, MainToolConfig][]).map((entry, index) => (
                        renderListItem(entry, index, handleMainFunctionOnClick)
                    ))}
                </TransitionGroup>
            </List>
        )
    }

    const renderGlossaryTools = () => {
        return (
            <List>
                {props.isOpen &&
                    <Slide direction={"right"} in={props.isOpen} mountOnEnter unmountOnExit>
                        <ListSubheader sx={{ opacity: props.isOpen ? 1 : 0, fontWeight: "1000", color: "black", lineHeight: "25px" }}>
                            {t('sideMenu.glossaryToolsHeader')}
                        </ListSubheader>
                    </Slide>
                }
                <ListItem disablePadding>
                    <ListItemButton sx={{ minHeight: 48, justifyContent: props.isOpen ? 'initial' : 'center', px: 2.5 }} onClick={buildGlossaryOnClick}>
                        <ListItemIcon sx={{ minWidth: 0, mr: props.isOpen ? 3 : 'auto', justifyContent: 'center', color: "black" }}>
                            <ConstructionIcon style={{ height: "24px", width: "24px", color: "black" }} />
                        </ListItemIcon>
                        <ListItemText primary={t('sideMenu.buildGlossary')} sx={{ opacity: props.isOpen ? 1 : 0 }} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton disabled={!isLoggedIn} sx={{ minHeight: 48, justifyContent: props.isOpen ? 'initial' : 'center', px: 2.5, paddingBottom: "0px", paddingTop: "0px" }} onClick={handleExpandMyGlossary}>
                        <ListItemIcon sx={{ minWidth: 0, mr: props.isOpen ? 3 : 'auto', justifyContent: 'center', color: "black" }}>
                            <BiBook style={{ height: "24px", width: "24px" }} />
                        </ListItemIcon>
                        <ListItemText primary={t('sideMenu.myGlossary')} sx={{ opacity: props.isOpen ? 1 : 0 }} />
                        {props.isOpen && (isExpand ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                </ListItem>
                <Collapse in={isExpand} timeout="auto" unmountOnExit>
                    <List
                        sx={{
                            paddingTop: "0px",
                            maxHeight: "320px",
                            overflowY: "auto",
                            overflowX: "clip",
                            "::-webkit-scrollbar": { width: "5px", transform: "rotate(180deg)" },
                            "::-webkit-scrollbar-thumb": { background: "black" }
                        }}
                    >
                        <TransitionGroup>
                            {
                                data?.map((glossary: qualityCheckMyGlossary) => (
                                    <Collapse key={glossary.myGlossaryId}>
                                        <ListItem disablePadding >
                                            <ListItemButton sx={{ padding: "0px" }} onClick={() => handleMyglossaryOnClick(glossary)}>
                                                <ClearIcon 
                                                    sx={{ height: "15px", width: "15px", cursor: "pointer", margin: "0px 30px 0px 24px" }} 
                                                    onClick={(event) => { 
                                                        event.stopPropagation(); 
                                                        event.preventDefault();
                                                        event.nativeEvent.stopImmediatePropagation();
                                                        setIsOpenDeleteConfirmDialog(true); 
                                                        setSelectedGlossaryId(glossary.myGlossaryId);
                                                    }} 
                                                />
                                                <ListItemText primary={glossary.glossaryName} title={glossary.glossaryName} />
                                            </ListItemButton>
                                        </ListItem>
                                    </Collapse>
                                ))
                            }
                        </TransitionGroup>

                    </List>
                </Collapse>
                <ListItem disablePadding>
                    <ListItemButton sx={{ minHeight: 48, justifyContent: props.isOpen ? 'initial' : 'center', px: 2.5 }} onClick={importMyGlossaryOnClick} disabled={!isLoggedIn}>
                        <ListItemIcon sx={{ minWidth: 0, mr: props.isOpen ? 3 : 'auto', justifyContent: 'center', color: "black" }}>
                            <FiUpload style={{ height: "24px", width: "24px", color: "black" }} />
                        </ListItemIcon>
                        <ListItemText primary={t('sideMenu.importGlossary')} sx={{ opacity: props.isOpen ? 1 : 0 }} />
                    </ListItemButton>
                    <input hidden accept={".xlsx"} type="file" ref={fileUploadFieldRef} onChange={handleMyGlossaryFileUpload} onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => (event.target as HTMLInputElement).value = ""} />
                </ListItem>
            </List>)
    }

    // const renderPayment = () => {

    //     const handlePaymentFunctionOnClick = (path: string) => {
    //         if (!props.isOpen) {
    //             props.toggleSideBar();
    //         }
    //         if (isLoggedIn) {
    //             navigate(path);
    //         } else {
    //             navigate("/login");
    //         }
    //     }

    //     return (
    //         <List>
    //             {props.isOpen &&
    //                 <Slide direction={"right"} in={props.isOpen} mountOnEnter unmountOnExit>
    //                     <ListSubheader sx={{ opacity: props.isOpen ? 1 : 0, fontWeight: "1000", color: "black", lineHeight: "25px" }}>
    //                         {"Payment"}
    //                     </ListSubheader>
    //                 </Slide>
    //             }
    //             {Object.entries(paymentPageSetting).map((entry, index) => (
    //                 renderListItem(entry, index, handlePaymentFunctionOnClick)
    //             ))}
    //         </List>
    //     )
    // }

    return (
        <Drawer
            open={props.isOpen}
            variant="permanent"
            sx={{
                '& .MuiDrawer-paper': {
                    top: "66px"
                },
                //  position: "absolute"
            }}
        >
            {deleteConfirmDialog()}
            <GlossaryPanel
                mode="edit"
                isOpen={isOpenEditGlossary}
                existingGlossary={selectedGlossaryItem}
                onClickClose={() => { setIsOpenEditGlossary(false); setSelectedGlossaryId(-1); }}
            />
            <GlossaryPanel
                mode="create"
                isOpen={isOpenCreateGlossary}
                onClickClose={() => { setIsOpenCreateGlossary(false) }}
                glossaryItems={glossaryList}
                expandMyGlossary={() => setIsExpand(true)}
                fileName={glossaryName}
                isBuildGlossary={false}
            />
            <BuildGlossaryPanel
                isOpen={isOpenBuildGlossaryPanel}
                onClose={() => { setIsOpenBuildGlossaryPanel(false) }}
                expandMyGlossary={() => setIsExpand(true)}
            />
            {renderMainFunction()}
            <Divider />
            {renderGlossaryTools()}
            {/* <Divider />
            {renderPayment()} */}
        </Drawer>
    )
}

export default SideMenu;
